/* eslint-disable @typescript-eslint/no-shadow */
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import { get, set } from 'lodash';
import { trims } from 'utils';
import { useModal } from 'modules/shared/hooks';
import { OperatingProcedures } from 'utils/enums';
import { logoutSelector, branchTitleSelector } from 'modules/common/LogoutBtn/selectors';
import YesNoModal from 'modules/shared/YesNoModal';
import ScanerBtn from 'modules/common/ScanerBtn';
import WeightBtn from 'modules/common/WeightBtn';
import { weigthSelector } from 'modules/common/WeightBtn/selectors';
import { Input, Checkbox, UpploadBtn } from 'modules/shared/Forms';
import { peripheralsSelector } from 'modules/common/Header/selectors';
import EditFlight from '../EditFlight';
import CommentModal from '../CommentModal';
import TrackInput from '../TrackInput';
import { Transport } from '../../../../../entities/Transport/interfaces';
import { Image } from '../../../../../entities/Photo/interfaces';
import { TripStatusIds, tripStatuses } from '../../../../../entities/Trip';
import { RolesMode } from '../../../../../entities/Roles';
import { postBlackListRequest } from '../../../../security/reports/ReportSpecialList/actions';
import { getTripRequest } from '../../../../common/CommonTrip/actions';

type Props = {
  statusId: TripStatusIds;
  handleChange: (e: React.ChangeEvent<any>) => void;
  mode: RolesMode;
  isVerified?: boolean;
  vehicleImages: Image[];
  trailerImages: Image[];
  setFieldValue: (name: string, files: any) => void;
  setFocus: any;
  isOptRequired?: boolean;
  operatingProcedure?: string | null;
};

type BLState = {
  type: number;
  licensePlate: string;
  isSMCTransport?: boolean;
};

const TransportInfoGrid: React.FC<Props> = ({
  mode,
  statusId,
  handleChange,
  isVerified = false,
  vehicleImages,
  trailerImages,
  setFieldValue,
  isOptRequired = false,
  setFocus,
  operatingProcedure,
}) => {
  const dispatch = useDispatch();
  const formik = useFormikContext();
  const [toBlackList, setToBlackList] = useState<string | BLState>('');
  const peripherals = useSelector(peripheralsSelector);
  const { isShow: isShowBLModal, openModal: openModalBLModal, closeModal: closeModalBLModal } = useModal();
  const { isShow: isShowСomment, openModal: openModalComment, closeModal: closeModalComment } = useModal();

  const { branchTitle } = useSelector(logoutSelector);
  const branchTitleData = useSelector(branchTitleSelector);
  const useTransportDoc: boolean = branchTitleData?.useTransportDoc ?? true;

  const isSecurity = mode === 'security';
  const isDirector = mode === 'director';
  const isOrdering = mode === 'ordering';
  const showForSecurity = [tripStatuses.create, tripStatuses.parkingInLine, tripStatuses.unloading].includes(statusId);

  const isManualGetWeight = peripherals?.find(
    (p) => p.type === 'Scales' && p.processName === 'Entrance',
  )?.isManualGetWeight;
  const [isEnableEntryVehicleWeight, setIsEnableEntryVehicleWeight] = useState<boolean | undefined>(isManualGetWeight);
  const { error } = useSelector(weigthSelector);
  useEffect(() => {
    if (error) {
      setIsEnableEntryVehicleWeight(true);
    }
  }, [error]);

  let isWeightFieldDisabled =
    operatingProcedure === OperatingProcedures.RulesOne
      ? (statusId !== tripStatuses.parkingInLine && isVerified) || isDirector
      : statusId > tripStatuses.waiting || isDirector;

  if (
    (operatingProcedure === OperatingProcedures.RulesTwo || operatingProcedure === OperatingProcedures.RulesThree) &&
    isSecurity &&
    statusId === tripStatuses.unloading
  ) {
    isWeightFieldDisabled = false;
  }

  const isDisabled =
    (isSecurity && !showForSecurity) || (statusId !== tripStatuses.parkingInLine && isVerified) || isDirector;

  // @ts-ignore
  const { tripIntention, trailer, vehicle, entryVehicleWeight } = formik.values;

  const shouldVerificationCheckboxBeDisplayedForVypiska = () => {
    if (operatingProcedure === OperatingProcedures.RulesOne) {
      return (+statusId > 1 && !isSecurity) || (isSecurity && +statusId > 2);
    }

    if (operatingProcedure === OperatingProcedures.RulesTwo) {
      return !isSecurity && +statusId >= 1 && +statusId < 4;
    }

    // RulesThree - isSecurity is always false
    return !isSecurity;
  };

  const disableCheckOrd =
    isDirector ||
    (isOrdering && !vehicle?.licensePlate) ||
    (isOrdering && useTransportDoc && !vehicle?.document?.number) ||
    (isOrdering && !entryVehicleWeight) ||
    (isOrdering && useTransportDoc && !vehicle?.document?.pictureFiles && !vehicle?.document?.pictures);

  const shouldTransportDocsCheckboxBeDisabledForRulesTwo = (
    isDirector: boolean,
    isOrdering: boolean,
    vehicle: Transport,
  ) => {
    if (isDirector) {
      return true;
    }

    if (isOrdering) {
      if (!vehicle?.licensePlate) {
        return true;
      }

      if (useTransportDoc && !vehicle?.document?.number) {
        return true;
      }

      if (useTransportDoc && (!vehicle?.document?.pictureFiles || vehicle?.document?.pictureFiles?.length === 0)) {
        return !vehicle?.document?.pictures || vehicle?.document?.pictures.length === 0;
      }
    }

    return false;
  };

  const shouldTransportDocsCheckboxBeDisabled = (isDirector: boolean, isOrdering: boolean, vehicle: Transport) => {
    if (operatingProcedure === OperatingProcedures.RulesOne) {
      return disableCheckOrd;
    }

    return shouldTransportDocsCheckboxBeDisabledForRulesTwo(isDirector, isOrdering, vehicle);
  };

  const carBlackList = get(formik.values, `vehicle.isBlackList`, false);
  const trailerBlackList = get(formik.values, `trailer.isBlackList`, false);

  const filesHandler = (type: 'vehicle' | 'trailer') => (event: ChangeEvent<HTMLInputElement>) => {
    const fieldName = `${type}.document.pictureFiles`;
    const isVehicle = type === 'vehicle';

    const images = [];
    // eslint-disable-next-line
    isVehicle ? images.push(...vehicleImages) : images.push(...trailerImages);

    // @ts-ignore
    setFieldValue(fieldName, [...images, ...event.currentTarget.files]);
  };

  const onToBlackList = (type: string) => {
    // @ts-ignore
    const { licensePlate } = formik.values[type];

    setToBlackList({ type: type === 'vehicle' ? 1 : 2, licensePlate });
    openModalBLModal();
  };

  const onAddBL = (data: any) => {
    dispatch(postBlackListRequest(data));
    // @ts-ignore
    setTimeout(() => dispatch(getTripRequest(formik.values?.id)), 500);
  };

  const onChangeDocuments = (e: any) => {
    set(e.target, 'value', trims(e.target.value));

    return handleChange(e);
  };

  useEffect(() => {
    if (tripIntention === '2' && statusId === tripStatuses.create) {
      // @ts-ignore
      if (!vehicle.document.pictures?.length || vehicle.document.pictureFiles?.length) {
        formik.setFieldError('vehicle', "Поле обов'язкове");
      }
    }
  }, [tripIntention]);

  return (
    <>
      <Col xs={12} sm={12} lg={8}>
        <Input
          name="vehicle.licensePlate"
          label="Номер автомобіля"
          requestParamName="licensePlate"
          isDisabledBtnDots={isDisabled}
          onChange={(e: React.ChangeEvent<any>) => {
            setFieldValue('vehicle.isBlackList', false);
            setFieldValue('vehicle.isSMCTransport', false);
            onChangeDocuments(e);
          }}
          maxLength={10}
          disabled={isDisabled}
          isRequired={
            (isSecurity && operatingProcedure === 'Порядок 1') ||
            (+statusId === 1 && operatingProcedure === 'Порядок 1') ||
            isOrdering
          }
        >
          <EditFlight
            clickHandler={() => onToBlackList('vehicle')}
            className="ml-1"
            icon="flag"
            disabled={isDisabled || !vehicle.licensePlate || vehicle.licensePlate.length < 2 || carBlackList}
          />
          {mode === 'security' && <EditFlight icon="camera" className="ml-1" disabled />}
        </Input>
        <TrackInput
          name="vehicle.document.number"
          label="Номер техпаспорту автомобіля"
          onChange={onChangeDocuments}
          maxLength={40}
          disabled={isDisabled}
          isRequired={((isSecurity && operatingProcedure === 'Порядок 1') || isOrdering) && useTransportDoc}
          onFocus={setFocus}
        >
          <div className="d-flex flex-row">
            <ScanerBtn
              typeDoc="vehicle"
              index={null}
              isDisabled={
                !peripherals?.length || isDisabled || !vehicle?.document?.number || vehicle?.document?.number.length < 2
              }
            />
            <UpploadBtn
              name="vehicle.document.pictureFiles"
              onChange={filesHandler('vehicle')}
              isDisabled={isDisabled || !vehicle?.document?.number || vehicle?.document?.number?.length < 2}
            />
          </div>
        </TrackInput>
        <Input
          name="trailer.licensePlate"
          requestParamName="licensePlate"
          isDisabledBtnDots={isDisabled}
          label="Номер причепа"
          onChange={(e: React.ChangeEvent<any>) => {
            setFieldValue('trailer.isBlackList', false);
            setFieldValue('trailer.isSMCTransport', false);
            handleChange(e);
          }}
          maxLength={8}
          disabled={isDisabled}
          isOptRequired={isOptRequired}
        >
          <EditFlight
            clickHandler={() => onToBlackList('trailer')}
            className="ml-1"
            icon="flag"
            disabled={!trailer?.licensePlate || trailer?.licensePlate.length < 2 || isDisabled || trailerBlackList}
          />
          {mode === 'security' && <EditFlight icon="camera" className="ml-1" disabled />}
        </Input>
        <TrackInput
          name="trailer.document.number"
          label="Номер техпаспорту причепа"
          onChange={handleChange}
          maxLength={40}
          disabled={isDisabled}
          isOptRequired={isOptRequired ? useTransportDoc : false}
          onFocus={setFocus}
        >
          <div className="d-flex flex-row">
            <ScanerBtn
              typeDoc="trailer"
              index={null}
              isDisabled={!trailer?.document?.number || trailer.document.number.length < 2 || isDisabled}
            />
            <UpploadBtn
              name="trailer.document.pictureFiles"
              onChange={filesHandler('trailer')}
              isDisabled={!trailer?.document?.number || trailer.document.number < 2 || isDisabled}
            />
          </div>
        </TrackInput>

        {(isSecurity || branchTitle?.operatingProcedure === OperatingProcedures.RulesOne) && (
          <Input
            name="entryVehicleWeight"
            type="number"
            step="1"
            pattern="\d+"
            placeholder="0"
            label="Зареєстрована вага автомобіля на в'їзді, кг"
            onChange={handleChange}
            minLength={1}
            maxLength={10}
            disabled={isWeightFieldDisabled || (!isWeightFieldDisabled && !isEnableEntryVehicleWeight)}
            isRequired={isSecurity || (operatingProcedure === 'Порядок 1' && isOrdering)}
            onFocus={() => setFocus('')}
          >
            {mode === 'security' && (
              <WeightBtn nameField="entryVehicleWeight" type="IN" isDisabled={isWeightFieldDisabled} />
            )}
          </Input>
        )}

        {(shouldVerificationCheckboxBeDisplayedForVypiska() || (isSecurity && +statusId > 2)) && (
          <div className="mt-5">
            <Checkbox
              name="isTransportDocumentValid"
              label="Документи верифіковано випискою"
              onChange={handleChange}
              disabled={isSecurity || shouldTransportDocsCheckboxBeDisabled(isDirector, isOrdering, vehicle)}
              isRequired={mode === 'ordering'}
              className="col-4 _req-checkbox"
            />
          </div>
        )}
      </Col>
      <YesNoModal
        isShow={isShowBLModal}
        title="Додати до чорного списку"
        acceptHandler={() => {
          openModalComment();
          closeModalBLModal();
        }}
        closeModal={closeModalBLModal}
      />
      <CommentModal
        isShow={isShowСomment}
        acceptHandler={onAddBL}
        id={toBlackList}
        type="vehicle"
        closeModal={closeModalComment}
      />
    </>
  );
};

export default TransportInfoGrid;
